import { useCallback, useEffect, useRef, useState } from "react";

type CustomScrollbarsProps = {
  children: React.ReactNode;
};

/**
 * custom scrollbars component that provides always-visible scrollbars
 */
export const CustomScrollbars: React.FC<CustomScrollbarsProps> = ({
  children,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const vThumbRef = useRef<HTMLDivElement>(null);
  const hThumbRef = useRef<HTMLDivElement>(null);

  const [vThumbHeight, setVThumbHeight] = useState(0);
  const [vThumbTop, setVThumbTop] = useState(0);
  const [hThumbWidth, setHThumbWidth] = useState(0);
  const [hThumbLeft, setHThumbLeft] = useState(0);

  const [isDraggingV, setIsDraggingV] = useState(false);
  const [isDraggingH, setIsDraggingH] = useState(false);
  const dragStartRef = useRef({
    scrollTop: 0,
    scrollLeft: 0,
    mouseX: 0,
    mouseY: 0,
  });

  // update scrollbar sizes and positions
  const updateScrollbars = useCallback(() => {
    const content = contentRef.current;
    if (!content) return;

    const scrollHeight = content.scrollHeight;
    const clientHeight = content.clientHeight;
    const scrollWidth = content.scrollWidth;
    const clientWidth = content.clientWidth;

    // vertical scrollbar
    if (scrollHeight > clientHeight) {
      const thumbHeight = Math.max(
        (clientHeight / scrollHeight) * clientHeight,
        30,
      );
      const maxScroll = scrollHeight - clientHeight;
      const thumbTop =
        (content.scrollTop / maxScroll) * (clientHeight - thumbHeight);
      setVThumbHeight(thumbHeight);
      setVThumbTop(thumbTop);
    } else {
      setVThumbHeight(0);
    }

    // horizontal scrollbar
    if (scrollWidth > clientWidth) {
      const thumbWidth = Math.max(
        (clientWidth / scrollWidth) * clientWidth,
        30,
      );
      const maxScroll = scrollWidth - clientWidth;
      const thumbLeft =
        (content.scrollLeft / maxScroll) * (clientWidth - thumbWidth);
      setHThumbWidth(thumbWidth);
      setHThumbLeft(thumbLeft);
    } else {
      setHThumbWidth(0);
    }
  }, []);

  // handle content scroll
  const handleContentScroll = useCallback(() => {
    if (!isDraggingV && !isDraggingH) {
      updateScrollbars();
    }
  }, [isDraggingV, isDraggingH, updateScrollbars]);

  // vertical thumb drag
  const handleVThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDraggingV(true);
    dragStartRef.current = {
      scrollTop: contentRef.current?.scrollTop || 0,
      scrollLeft: 0,
      mouseX: 0,
      mouseY: e.clientY,
    };
  };

  // horizontal thumb drag
  const handleHThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDraggingH(true);
    dragStartRef.current = {
      scrollTop: 0,
      scrollLeft: contentRef.current?.scrollLeft || 0,
      mouseX: e.clientX,
      mouseY: 0,
    };
  };

  // handle mouse move for dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const content = contentRef.current;
      if (!content) return;

      if (isDraggingV) {
        const deltaY = e.clientY - dragStartRef.current.mouseY;
        const scrollHeight = content.scrollHeight;
        const clientHeight = content.clientHeight;
        const maxScroll = scrollHeight - clientHeight;
        const scrollRatio = maxScroll / (clientHeight - vThumbHeight);
        content.scrollTop =
          dragStartRef.current.scrollTop + deltaY * scrollRatio;
        updateScrollbars();
      }

      if (isDraggingH) {
        const deltaX = e.clientX - dragStartRef.current.mouseX;
        const scrollWidth = content.scrollWidth;
        const clientWidth = content.clientWidth;
        const maxScroll = scrollWidth - clientWidth;
        const scrollRatio = maxScroll / (clientWidth - hThumbWidth);
        content.scrollLeft =
          dragStartRef.current.scrollLeft + deltaX * scrollRatio;
        updateScrollbars();
      }
    };

    const handleMouseUp = () => {
      setIsDraggingV(false);
      setIsDraggingH(false);
    };

    if (isDraggingV || isDraggingH) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDraggingV, isDraggingH, vThumbHeight, hThumbWidth, updateScrollbars]);

  // update on mount and content changes
  useEffect(() => {
    updateScrollbars();
    const resizeObserver = new ResizeObserver(updateScrollbars);
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [updateScrollbars, children]);

  return (
    <>
      <div className="table-scroll-wrapper">
        <div
          ref={contentRef}
          className="table-content-scroll"
          onScroll={handleContentScroll}
        >
          {children}
        </div>
        {vThumbHeight > 0 && (
          <div className="custom-vertical-scrollbar">
            <div
              ref={vThumbRef}
              className={`scrollbar-thumb ${isDraggingV ? "dragging" : ""}`}
              style={{ height: vThumbHeight, top: vThumbTop }}
              onMouseDown={handleVThumbMouseDown}
            />
          </div>
        )}
      </div>
      {hThumbWidth > 0 && (
        <div className="custom-horizontal-scrollbar">
          <div
            ref={hThumbRef}
            className={`scrollbar-thumb ${isDraggingH ? "dragging" : ""}`}
            style={{ width: hThumbWidth, left: hThumbLeft }}
            onMouseDown={handleHThumbMouseDown}
          />
        </div>
      )}
    </>
  );
};
